import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../components/ui/Button';
import { Input, Select } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import {
  Criticidad,
  CRITICIDADES,
  type CriticidadValor,
} from '../../../domain/value-objects/Criticidad';
import {
  describirVariable,
  VARIABLES_MEDIDOR,
} from '../../../domain/value-objects/VariableMedidor';
import type { Medidor } from '../../../domain/entities/Medidor';
import type {
  DatosUmbralEnergia,
  UmbralEnergia,
} from '../../../data/repositories/RepositorioEnergiaUmbrales';
import estilos from './Umbrales.module.css';

interface FormularioUmbralEnergiaProps {
  abierto: boolean;
  umbral: UmbralEnergia | null;
  medidores: readonly Medidor[];
  guardando: boolean;
  onCerrar: () => void;
  onGuardar: (datos: DatosUmbralEnergia) => void;
}

interface Campos {
  medidorId: string;
  variable: string;
  etiqueta: string;
  minimo: string;
  maximo: string;
  criticidad: CriticidadValor;
  descripcion: string;
  activo: boolean;
}

const CAMPOS_INICIALES: Campos = {
  medidorId: '',
  variable: '',
  etiqueta: '',
  minimo: '',
  maximo: '',
  criticidad: 'media',
  descripcion: '',
  activo: true,
};

function aNumeroOpcional(texto: string): number | null {
  const t = texto.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/** Alta y edición de un umbral sobre una magnitud de un medidor. */
export function FormularioUmbralEnergia({
  abierto,
  umbral,
  medidores,
  guardando,
  onCerrar,
  onGuardar,
}: FormularioUmbralEnergiaProps) {
  const [campos, setCampos] = useState<Campos>(CAMPOS_INICIALES);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!abierto) return;

    setError(null);
    setCampos(
      umbral
        ? {
            medidorId: umbral.medidorId,
            variable: umbral.variable,
            etiqueta: umbral.variableEtiqueta ?? '',
            minimo: umbral.valorMinimo?.toString() ?? '',
            maximo: umbral.valorMaximo?.toString() ?? '',
            criticidad: umbral.criticidad.valor,
            descripcion: umbral.descripcion ?? '',
            activo: umbral.activo,
          }
        : CAMPOS_INICIALES,
    );
  }, [abierto, umbral]);

  /** La unidad se toma del catálogo para orientar al escribir los límites. */
  const unidad = useMemo(() => describirVariable(campos.variable).unidad, [campos.variable]);

  const enviar = () => {
    setError(null);

    const medidor = medidores.find((m) => m.id === campos.medidorId);
    if (!medidor) {
      setError('Elige el medidor al que se aplica este umbral.');
      return;
    }
    if (medidor.clienteId === null) {
      setError('Ese medidor no tiene cliente asignado, así que no puede tener umbrales.');
      return;
    }
    if (!campos.variable) {
      setError('Elige qué magnitud se va a vigilar.');
      return;
    }

    const minimo = aNumeroOpcional(campos.minimo);
    const maximo = aNumeroOpcional(campos.maximo);

    if (minimo === null && maximo === null) {
      setError('Indica al menos un límite: mínimo, máximo o los dos.');
      return;
    }
    if (minimo !== null && maximo !== null && minimo >= maximo) {
      setError('El mínimo debe ser menor que el máximo.');
      return;
    }

    onGuardar({
      meter_id: medidor.id,
      cliente_id: medidor.clienteId,
      variable: campos.variable,
      variable_label: campos.etiqueta.trim() || null,
      min_value: minimo,
      max_value: maximo,
      criticidad: campos.criticidad,
      activo: campos.activo,
      descripcion: campos.descripcion.trim() || null,
    });
  };

  return (
    <Modal
      abierto={abierto}
      onCerrar={onCerrar}
      titulo={umbral ? 'Editar umbral de energía' : 'Nuevo umbral de energía'}
      descripcion="Se avisa cuando la magnitud del medidor sale del rango indicado."
      pie={
        <>
          <Button variante="secundario" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button onClick={enviar} cargando={guardando} icono="guardar">
            Guardar
          </Button>
        </>
      }
    >
      <div className={estilos.formulario}>
        {error && <div className={estilos.errorFormulario}>{error}</div>}

        <div className={estilos.filaCampos}>
          <Select
            etiqueta="Medidor"
            requerido
            placeholder="Elige un medidor"
            value={campos.medidorId}
            onChange={(e) => setCampos((c) => ({ ...c, medidorId: e.target.value }))}
            opciones={medidores.map((m) => ({
              valor: m.id,
              etiqueta: m.clienteNombre ? `${m.nombre} · ${m.clienteNombre}` : m.nombre,
            }))}
          />

          <Select
            etiqueta="Qué vigilar"
            requerido
            placeholder="Elige una magnitud"
            value={campos.variable}
            onChange={(e) => setCampos((c) => ({ ...c, variable: e.target.value }))}
            opciones={VARIABLES_MEDIDOR.map((v) => ({
              valor: v.clave,
              etiqueta: v.unidad ? `${v.etiqueta} (${v.unidad})` : v.etiqueta,
            }))}
          />
        </div>

        <div className={estilos.filaCampos}>
          <Input
            etiqueta={unidad ? `Valor mínimo (${unidad})` : 'Valor mínimo'}
            type="number"
            step="any"
            placeholder="Sin mínimo"
            value={campos.minimo}
            onChange={(e) => setCampos((c) => ({ ...c, minimo: e.target.value }))}
            ayuda="Por debajo de este valor se avisa"
          />
          <Input
            etiqueta={unidad ? `Valor máximo (${unidad})` : 'Valor máximo'}
            type="number"
            step="any"
            placeholder="Sin máximo"
            value={campos.maximo}
            onChange={(e) => setCampos((c) => ({ ...c, maximo: e.target.value }))}
            ayuda="Por encima de este valor se avisa"
          />
        </div>

        <div className={estilos.filaCampos}>
          <Select
            etiqueta="Gravedad"
            value={campos.criticidad}
            onChange={(e) =>
              setCampos((c) => ({ ...c, criticidad: e.target.value as CriticidadValor }))
            }
            opciones={CRITICIDADES.map((c) => ({
              valor: c,
              etiqueta: Criticidad.from(c).etiqueta,
            }))}
          />
          <Input
            etiqueta="Nombre propio"
            placeholder="Opcional"
            value={campos.etiqueta}
            onChange={(e) => setCampos((c) => ({ ...c, etiqueta: e.target.value }))}
            ayuda="Cómo llamar a esta magnitud en los avisos"
          />
        </div>

        <Input
          etiqueta="Descripción"
          placeholder="Opcional: qué debe hacer quien reciba el aviso"
          value={campos.descripcion}
          onChange={(e) => setCampos((c) => ({ ...c, descripcion: e.target.value }))}
        />

        <div className={estilos.opciones}>
          <label className={estilos.opcion}>
            <input
              type="checkbox"
              checked={campos.activo}
              onChange={(e) => setCampos((c) => ({ ...c, activo: e.target.checked }))}
            />
            <span>
              <strong>Umbral activo</strong>
              <span className={estilos.opcionAyuda}>Si se desactiva, deja de vigilarse</span>
            </span>
          </label>
        </div>
      </div>
    </Modal>
  );
}
